import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { usePostHog } from 'posthog-js/react';

export function Landing() {
  const [username, setUsername] = useState('');
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const posthog = usePostHog();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    const trimmedUsername = username.trim();
    
    if (!trimmedUsername) {
      setError('Please enter a username');
      return;
    }
    
    if (trimmedUsername.length < 2) {
      setError('Username must be at least 2 characters');
      return;
    }
    
    if (trimmedUsername.length > 20) {
      setError('Username must be 20 characters or less');
      return;
    }
    
    // Store username in session storage
    sessionStorage.setItem('username', trimmedUsername);
    
    // Identify user in PostHog
    posthog?.identify(trimmedUsername);
    posthog?.capture('user_set_username', { username: trimmedUsername });
    
    // Navigate to lobby
    navigate('/lobby');
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setUsername(e.target.value);
    if (error) setError('');
  };

  // Check for wide gamut display
  React.useEffect(() => {
    if (window.matchMedia('(color-gamut: p3)').matches) {
      console.info('Wide gamut display detected. Colors will be shown in sRGB for fairness.');
    }
  }, []);

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-md"
      >
        <div className="bg-white rounded-lg shadow-lg p-8">
          <div className="text-center mb-8">
            <h1 className="text-4xl font-bold text-gray-900 mb-2">Guess Hue</h1>
            <p className="text-gray-600">A multiplayer color guessing game</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label htmlFor="username" className="block text-sm font-medium text-gray-700 mb-2">
                Choose your username
              </label>
              <input
                type="text"
                id="username"
                value={username}
                onChange={handleChange}
                placeholder="Enter username..."
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-colors text-lg"
                autoFocus
                maxLength={20}
              />
            </div>

            {error && (
              <motion.p
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-red-600 text-sm"
              >
                {error}
              </motion.p>
            )}

            <button
              type="submit"
              className="w-full px-6 py-3 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors font-medium text-lg"
            >
              Continue to Lobby
            </button>
          </form>

          <div className="mt-8 text-center text-sm text-gray-500">
            <p>No account needed • Just pick a name and play!</p>
          </div>
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1 }}
          className="mt-4 text-center text-xs text-gray-400"
        >
          <p>Colors displayed in sRGB for consistent gameplay</p>
        </motion.div>
      </motion.div>
    </div>
  );
}